import lodashSnakeCase from 'lodash-es/snakeCase';
import lodashUniq from 'lodash-es/uniq';

import { formatMileage, formatTimeRemaining } from './shared';

import { type SaleAssetBusEntity, type SaleAssetBusListingItem, SaleType, type UserMeta } from '~/apiClient';
import { isAuction, isSaleListingItemEntity, type SaleItemEntity, type SaleListingItemEntity } from '~/types/sale.type';

type TrackingVehicle = {
    vin: string;
    first_registration: string;
    mileage: string;
    engine: string | undefined;
    condition: string | null;
    model: string;
};

type TrackingSale = {
    id: string;
    name: string;
    brand: string;
    classes: string | null;
    type: string;
    status: string;
    bundle: string;
    list_name: string | null;
    value: number | null;
    currency: string;
    index: number;
    reserve_price_reached: string | null;
    time_remaining: string | null;
    negotiation: string | null;
    selling_method: string;
    vehicles: TrackingVehicle[];
};

const formatVehicleCondition = (asset: SaleAssetBusEntity | SaleAssetBusListingItem, saleType: SaleType): string | null => {
    if (getSaleTypeInfo(saleType).isToBeScraped) return 'to_be_scrapped';

    if (asset.showBadgeInspectionExpired) return 'inspection_expired';

    if (asset.showBadgeInspectionExpiringSoon) return 'inspection_expiring_soon';

    return null;
};

const formatSaleName = (sale: SaleItemEntity | SaleListingItemEntity): string => {
    const { isBundleMono, isBundleMulti } = getSaleTypeInfo(sale.type);
    const nAssets = isSaleListingItemEntity(sale) ? sale.assetCount : sale.assets.length;

    if (isBundleMulti) return `Bundle of ${nAssets} vehicles`;

    const { mainAsset } = sale;

    return `${isBundleMono ? `N. ${nAssets} ` : ''}${mainAsset.brand.brandValue} ${mainAsset.model.modelValue}`;
};

const formatSaleClasses = (sale: SaleItemEntity | SaleListingItemEntity): string | null => {
    const classes = 'assets' in sale ? sale.assets.map(asset => asset.class) : [sale.mainAsset.class];
    const uniqueNonNullClasses = lodashUniq(classes).filter(Boolean);

    return uniqueNonNullClasses.length ? uniqueNonNullClasses.join(' | ') : null;
};

const formatReservePriceReached = (userMeta: UserMeta | undefined): string | null =>
    typeof userMeta?.reservePriceReached === 'boolean' ? String(userMeta.reservePriceReached) : null;

const formatVehicle = (asset: SaleAssetBusEntity | SaleAssetBusListingItem, saleType: SaleType): TrackingVehicle => {
    const dayjs = useDayjs();

    return {
        vin: asset.vin,
        first_registration: dayjs(asset.firstRegistration).format('MM/YYYY'),
        mileage: formatMileage(asset.km),
        engine: asset.euroNorm ? lodashSnakeCase(asset.euroNorm) : undefined,
        condition: formatVehicleCondition(asset, saleType),
        model: asset.model.modelValue,
    };
};

export const formatSale = (sale: SaleItemEntity | SaleListingItemEntity, index: number, listName: string | null, value?: number): TrackingSale => ({
    id: sale.id,
    name: formatSaleName(sale),
    brand: sale.mainAsset.brand.brandValue,
    classes: formatSaleClasses(sale),
    type: lodashSnakeCase(sale.mainAsset.assetType),
    status: lodashSnakeCase(sale.state),
    bundle: String(getSaleTypeInfo(sale.type).isBundle),
    list_name: listName,
    value: value ?? (isAuction(sale) ? sale.currentPrice : sale.price) ?? null,
    currency: sale.currency,
    index: index + 1,
    ...(isAuction(sale)
        ? {
              reserve_price_reached: formatReservePriceReached(sale.userMeta),
              time_remaining: sale.expiresAt ? formatTimeRemaining(sale.expiresAt) : null,
              negotiation: String(!!sale.negotiation),
          }
        : {
              reserve_price_reached: null,
              time_remaining: null,
              negotiation: String(!!sale.negotiations?.length),
          }),
    selling_method: lodashSnakeCase(sale.sellingMethod),
    vehicles: 'assets' in sale ? sale.assets.map(asset => formatVehicle(asset, sale.type)) : [formatVehicle(sale.mainAsset, sale.type)],
});
